import { useCallback, useEffect, useRef, useState } from 'react'

const NONE = []

// A node area's "Available on" filter (Content, Templates): the platform
// chosen, and the area's list as content answers it for that platform
// (GET /node-course?platform=web: the courses available there and every
// folder above one of them). The platform belongs to the area, not to one
// folder's page, so it holds while people drill down.
//
// `useApiList` is the area's list hook (useApiListNodeCourse, ...). Only the
// answer for the platform chosen now is ever shown: a slower answer for a
// platform chosen earlier is dropped. Until it is in, `settled` is false; a
// failed read settles with its `error` (the api-new thrown response, which
// carries the request's id for ErrorReference).
export const usePlatformList = useApiList => {
  // api-new hooks have a new identity every render: read through a ref.
  const api = useApiList()
  const apiRef = useRef(api)
  apiRef.current = api

  const [platform, setPlatformState] = useState(null)
  const [answer, setAnswer] = useState(null)
  const [attempt, setAttempt] = useState(0)

  useEffect(
    () => {
      if (!platform) return
      let live = true
      Promise.resolve()
        .then(() => apiRef.current.get(`?platform=${encodeURIComponent(platform)}`))
        .then(items => ({ platform, attempt, items: Array.isArray(items) ? items : [] }), error => ({ platform, attempt, items: [], error: error || {} }))
        .then(next => {
          if (live) setAnswer(next)
        })
      return () => {
        live = false
      }
    },
    [platform, attempt]
  )

  const current = platform && answer && answer.platform === platform && answer.attempt === attempt ? answer : null

  const setPlatform = useCallback(next => setPlatformState(next || null), [])
  // Asks again (Try again, or after an edit), showing the loading state
  // meanwhile rather than the old answer.
  const reload = useCallback(() => setAttempt(a => a + 1), [])

  return {
    platform,
    setPlatform,
    settled: !!current,
    items: current ? current.items : NONE,
    error: current ? current.error : undefined,
    reload
  }
}
